/**
 * Full-page search results — supports up to 25 per group with a category filter.
 * URL: /search?q=xyz&type=recipes|cookbooks|...
 * The `type` query filter maps to one of the API groups. Missing/invalid = "all".
 */
import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import PageMeta from "@/components/PageMeta";
import { MagnifyingGlass, BookOpen, Newspaper, HourglassHigh, Storefront, Question, Tag, ForkKnife, BookBookmark } from "@phosphor-icons/react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useVoiceCommand, useVoiceSearch } from "@/contexts/VoiceSearchContext";
import { speak, sayOnceThenBeep } from "@/lib/voice";
import { cookbookPath } from "@/lib/slug";
import { SearchFilterChips } from "@/components/search/SearchFilterChips";
import { DietFilterChips } from "@/components/search/DietFilterChips";
import { DietProfileNote } from "@/components/DietProfileNote";
import { dietListLabel } from "@/lib/diets";
import { SaveCollectionDialog, saveCollection } from "@/components/search/SaveCollectionDialog";
import { AddToCookbookButton, addToCookbook, notifyAdded } from "@/components/search/AddToCookbookButton";
import { FILTER_RE, SAVE_RE, PICK_RE, ADD_RE, parseFilters, parseSaveTitle, parsePick, parseAdd, applyFilters } from "@/utils/searchVoice";

const GROUP_META = {
  recipes:     { label: "Recipes",              Icon: ForkKnife },
  cookbooks:   { label: "Cookbooks",            Icon: BookBookmark },
  historical:  { label: "Historical recipes",   Icon: HourglassHigh },
  news:        { label: "News",                 Icon: Newspaper },
  ingredients: { label: "Ingredients",          Icon: BookOpen },
  tags:        { label: "Tags",                 Icon: Tag },
  shop:        { label: "Shop",                 Icon: Storefront },
  faqs:        { label: "FAQs",                 Icon: Question },
};
const GROUP_ORDER = ["recipes", "cookbooks", "historical", "news", "ingredients", "tags", "shop", "faqs"];

export default function Search() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const q0 = params.get("q") || "";
  const type = params.get("type") || "all";
  const { user } = useAuth();
  const profileDiets = user?.diet_profile || [];
  const usingProfile = !params.has("diet") && profileDiets.length > 0;
  const filters = useMemo(() => ({
    src: params.get("src") || null,
    max: parseInt(params.get("max") || "", 10) || null,
    easy: params.get("easy") === "1",
    diets: params.has("diet") ? (params.get("diet") || "").split(",").filter(Boolean) : profileDiets,
  }), [params, profileDiets.join()]); // eslint-disable-line react-hooks/exhaustive-deps
  const anyOn = (f) => !!(f.src || f.max || f.easy || f.diets?.length);
  const filtersOn = anyOn(filters);
  const browseDiet = params.get("browse") ? filters.diets.join(",") : "";
  const [saveOpen, setSaveOpen] = useState(false);

  const [inputVal, setInputVal] = useState(q0);
  const [data, setData] = useState({ groups: {}, total: 0, query: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => setInputVal(q0), [q0]);

  useEffect(() => {
    const term = q0.trim();
    if (term.length < 2) {
      setData({ groups: {}, total: 0, query: "" });
      return;
    }
    setLoading(true);
    api.get(`/search?q=${encodeURIComponent(term)}&limit=60${params.get("browse") ? `&browse=true&diet=${encodeURIComponent(browseDiet)}` : ""}`)
      .then((r) => setData(r.data || { groups: {}, total: 0, query: term }))
      .catch(() => setData({ groups: {}, total: 0, query: term }))
      .finally(() => setLoading(false));
  }, [q0, browseDiet]); // eslint-disable-line react-hooks/exhaustive-deps

  const visibleKeys = useMemo(() => {
    if (filtersOn) return ["recipes", "historical"].filter((k) => type === "all" || type === k);
    if (type !== "all" && GROUP_META[type]) return [type];
    return GROUP_ORDER;
  }, [type, filtersOn]);

  const groupItems = useCallback((key, f = filters) => {
    const items = data.groups?.[key] || [];
    return (key === "recipes" || key === "historical") && anyOn(f) ? applyFilters(items, f) : items;
  }, [data, filters]);
  // Numbered list the voice reads + picks from (what's on screen).
  const pickList = useMemo(() => ["recipes", "historical"].filter((k) => visibleKeys.includes(k)).flatMap((k) => groupItems(k)), [visibleKeys, groupItems]);

  const setFilters = (f) => {
    const next = new URLSearchParams(params);
    ["src", "max", "easy", "diet"].forEach((k) => next.delete(k));
    next.set("diet", (f.diets || []).join(","));
    if (f.src) next.set("src", f.src);
    if (f.max) next.set("max", String(f.max));
    if (f.easy) next.set("easy", "1");
    setParams(next);
  };

  // ---- Voice: filters / save / pick (priority so they beat global routing) ----
  const live = useRef({});
  live.current = { data, filters, pickList, q0, user };
  const onVoiceFilter = useCallback((txt) => {
    const parsed = parseFilters(txt);
    if (!parsed) return;
    const { data: d, filters: cur, q0: q } = live.current;
    const f = parsed.clear ? {} : { ...cur, ...parsed, diets: parsed.diets ? [...new Set([...(cur.diets || []), ...parsed.diets])] : cur.diets };
    setFilters(f);
    const list = ["recipes", "historical"].flatMap((k) => (anyOn(f) ? applyFilters(d.groups?.[k] || [], f) : (d.groups?.[k] || [])));
    const bits = [];
    if (f.diets?.length) bits.push(dietListLabel(f.diets));
    if (f.src === "mine") bits.push("of yours");
    if (f.src === "shared") bits.push("shared with you");
    if (f.src === "public") bits.push("from the directory");
    if (f.src === "historical") bits.push("historical");
    if (f.max) bits.push(`under ${f.max} minutes`);
    if (f.easy) bits.push("easy");
    if (!list.length) { sayOnceThenBeep(`No ${q} recipes ${bits.join(", ")}. Say "show everything" to reset.`); return; }
    const top = list.slice(0, 5).map((it, i) => `${i + 1}, ${it.title}`).join(". ");
    speak(`${list.length} ${q} recipe${list.length === 1 ? "" : "s"}${bits.length ? ` ${bits.join(", ")}` : ""}. Top ones: ${top}. Say a number to open one.`);
  }, [params]); // eslint-disable-line react-hooks/exhaustive-deps
  const onVoiceSave = useCallback(async (txt) => {
    const { pickList: list, user: u } = live.current;
    const title = parseSaveTitle(txt);
    if (!u) { speak("Please sign in first so I can save these to a cookbook."); return; }
    if (!title || !list.length) { speak("There's nothing on screen to save."); return; }
    speak(`Saving ${list.length} recipes as ${title}.`);
    try {
      const res = await saveCollection(title, list);
      toast.success(`Saved ${res.saved} recipe${res.saved === 1 ? "" : "s"} to “${res.title}”`);
      if (res.limit_hit) { toast.warning(res.limit_hit); speak(`Saved ${res.saved}. ${res.limit_hit}`); }
      else speak(`Done. ${res.saved} recipes saved to ${res.title}.`);
      navigate(cookbookPath(res.cookbook_id, res.title));
    } catch (e) {
      const msg = e?.response?.data?.detail || "Sorry, I couldn't save that.";
      toast.error(msg); speak(msg);
    }
  }, [navigate]);
  const onVoicePick = useCallback((txt) => {
    const i = parsePick(txt);
    const it = live.current.pickList[i];
    if (!it) { speak(`There's no number ${i + 1} on screen.`); return; }
    speak(`Opening ${it.title}`);
    navigate(it.url);
  }, [navigate]);
  const { setPriorityHandler } = useVoiceSearch();
  const runAdd = useCallback(async (payload, items) => {
    try {
      const res = await addToCookbook(payload, items);
      if (res.status === "not_found") {
        speak(`I couldn't find ${res.name}. Shall I create it? Say yes or no.`);
        setPriorityHandler((reply) => {
          if (/^(?:yes|yeah|yep|sure|ok(?:ay)?|please|do it|create it|go on)\b/.test(reply)) {
            setPriorityHandler(null);
            runAdd({ ...payload, create: true }, items);
            return true;
          }
          if (/^(?:no|nope|don'?t|cancel|never ?mind)\b/.test(reply)) {
            setPriorityHandler(null);
            speak("Okay, I won't create it.");
            return true;
          }
          return false;
        });
        return;
      }
      notifyAdded(res);
      speak(res.limit_hit
        ? `Added ${res.saved} to ${res.title}. ${res.limit_hit}`
        : `Added ${res.saved === 1 ? items[0].title : `${res.saved} recipes`} to ${res.title}.`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Sorry, I couldn't add that.";
      toast.error(msg); speak(msg);
    }
  }, [setPriorityHandler]);
  const onVoiceAdd = useCallback((txt) => {
    const { pickList: list, user: u } = live.current;
    const parsed = parseAdd(txt);
    if (!parsed) return;
    if (!u) { speak("Please sign in first so I can add recipes to your cookbook."); return; }
    const items = parsed.all ? list : parsed.indices.map((i) => list[i]).filter(Boolean);
    if (!items.length) { speak("I couldn't find those numbers on screen."); return; }
    runAdd({ cookbook_name: parsed.name }, items);
  }, [runAdd]);
  useVoiceCommand("search-add-cookbook", ADD_RE, onVoiceAdd, "Add to cookbook", { priority: true, keepListening: true, chainPrompt: false });
  useVoiceCommand("search-filter", FILTER_RE, onVoiceFilter, "Filter results", { priority: true, keepListening: true, chainPrompt: false });
  useVoiceCommand("search-save", SAVE_RE, onVoiceSave, "Save as cookbook", { priority: true });
  useVoiceCommand("search-pick", PICK_RE, onVoicePick, "Open result", { priority: true });

  const submit = (e) => {
    e.preventDefault();
    const t = inputVal.trim();
    const next = new URLSearchParams();
    if (t) next.set("q", t);
    if (type !== "all") next.set("type", type);
    setParams(next);
  };

  const pageTitle = q0.trim() ? `Search: ${q0.trim()}` : "Search";

  return (
    <>
      <PageMeta
        title={`${pageTitle} · RecipeFiler`}
        description="Search across every recipe, cookbook, historical dish, news post, shop item and FAQ on RecipeFiler."
        canonical="/search"
        keywords="search Recipe Filer, find a recipe, find a cookbook, search historical recipes, recipe search UK, ingredient recipe search"
        noindex={Boolean(q0.trim())}
      />
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <h1 className="text-3xl md:text-4xl font-black font-display text-rf-ink mb-6">
          {q0.trim() ? <>Results for <span className="text-rf-orange">"{q0.trim()}"</span></> : "Search RecipeFiler"}
        </h1>

        {/* Search input */}
        <form onSubmit={submit} className="flex items-center gap-2 bg-white rounded-full border border-rf-ink/15 shadow-sm px-4 py-2.5 mb-6">
          <MagnifyingGlass size={20} weight="bold" className="text-rf-ink-soft shrink-0" />
          <input
            data-testid="search-page-input"
            value={inputVal}
            onChange={(e) => setInputVal(e.target.value)}
            placeholder="Search recipes, cookbooks, ingredients, news, historical, shop, FAQs…"
            className="flex-1 bg-transparent outline-none text-rf-ink placeholder:text-rf-ink-soft/60"
            autoFocus
          />
          <button type="submit" className="btn-amber !py-1.5 !px-4 text-sm">Search</button>
        </form>

        {/* "Did you mean" chip strip — only shown when the synonym
            engine added terms AND those terms actually earned at
            least one result. Clicking a chip re-runs the search with
            that term so the user can narrow down. */}
        {Array.isArray(data?.expansion?.extras) && data.expansion.extras.length > 0 && (data.total || 0) > 0 && (
          <div
            data-testid="search-synonym-chips"
            className="flex flex-wrap items-center gap-1.5 mb-5 rounded-2xl bg-rf-cream-alt/70 border border-rf-ink/10 px-4 py-3"
          >
            <span className="text-[11px] uppercase tracking-wider font-bold text-rf-ink-soft shrink-0 mr-1">
              Also matched:
            </span>
            {data.expansion.extras.map((t) => (
              <button
                key={t}
                type="button"
                data-testid={`search-chip-${t}`}
                onClick={() => {
                  setInputVal(t);
                  const next = new URLSearchParams(); next.set("q", t);
                  if (type !== "all") next.set("type", type);
                  setParams(next);
                }}
                className="text-xs font-bold px-2.5 py-1 rounded-full bg-white border border-rf-ink/15 text-rf-ink hover:bg-rf-amber/10 hover:border-rf-amber transition-colors"
                title={`Search for "${t}" instead`}
              >
                {t}
              </button>
            ))}
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-1.5 mb-8">
          <button
            data-testid="search-filter-all"
            onClick={() => {
              const next = new URLSearchParams(); if (q0) next.set("q", q0); setParams(next);
            }}
            className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${type === "all" ? "bg-rf-ink text-rf-cream border-rf-ink" : "bg-white text-rf-ink-soft border-rf-ink/15 hover:border-rf-ink/40"}`}
          >
            All {data.total ? `(${data.total})` : ""}
          </button>
          {GROUP_ORDER.map((key) => {
            const count = (data.groups?.[key] || []).length;
            if (!count && type !== key) return null;
            const { label } = GROUP_META[key];
            return (
              <button
                key={key}
                data-testid={`search-filter-${key}`}
                onClick={() => {
                  const next = new URLSearchParams(); if (q0) next.set("q", q0); next.set("type", key); setParams(next);
                }}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${type === key ? "bg-rf-ink text-rf-cream border-rf-ink" : "bg-white text-rf-ink-soft border-rf-ink/15 hover:border-rf-ink/40"}`}
              >
                {label} {count ? `(${count})` : ""}
              </button>
            );
          })}
        </div>

        {usingProfile && q0.trim().length >= 2 && (
          <DietProfileNote diets={profileDiets} onShowAll={() => setFilters({ ...filters, diets: [] })} />
        )}
        {q0.trim().length >= 2 && ((data.groups?.recipes || []).length + (data.groups?.historical || []).length) > 0 && (
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <SearchFilterChips filters={filters} onChange={setFilters} signedIn={!!user} />
              <DietFilterChips
                items={applyFilters([...(data.groups?.recipes || []), ...(data.groups?.historical || [])], { ...filters, diets: [] })}
                filters={filters}
                onChange={setFilters}
              />
            </div>
            {user && pickList.length > 0 && (
              <button type="button" onClick={() => setSaveOpen(true)} data-testid="search-save-collection" className="btn-outline-ink !py-1.5 !px-3 text-xs">
                Save {pickList.length} as cookbook
              </button>
            )}
          </div>
        )}
        <SaveCollectionDialog
          open={saveOpen} onOpenChange={setSaveOpen}
          defaultTitle={`My ${q0.trim().replace(/\b\w/g, (c) => c.toUpperCase())} List`}
          items={pickList}
        />

        {/* Results */}
        {q0.trim().length < 2 ? (
          <div className="text-sm text-rf-ink-soft py-8">Type at least 2 characters to search.</div>
        ) : loading ? (
          <div className="text-sm text-rf-ink-soft py-8">Searching…</div>
        ) : data.total === 0 ? (
          <div className="text-center py-12">
            <div className="text-lg font-bold text-rf-ink mb-1">No results found</div>
            <div className="text-sm text-rf-ink-soft">Try a broader term like an ingredient name or a tag.</div>
          </div>
        ) : filtersOn && pickList.length === 0 ? (
          <div className="text-center py-12" data-testid="search-filters-empty">
            <div className="text-lg font-bold text-rf-ink mb-1">No recipes match these filters</div>
            <button type="button" onClick={() => setFilters({})} className="text-sm text-rf-orange font-bold underline">Show everything</button>
          </div>
        ) : (
          <div className="space-y-8">
            {visibleKeys.map((key) => {
              const items = groupItems(key);
              if (!items.length) return null;
              const { label, Icon } = GROUP_META[key];
              return (
                <section key={key} data-testid={`search-section-${key}`}>
                  <h2 className="flex items-center gap-2 text-xl font-black font-display text-rf-ink mb-3">
                    <Icon size={20} weight="duotone" className="text-rf-orange" />
                    {label} <span className="text-sm font-semibold text-rf-ink-soft ml-1">({items.length})</span>
                  </h2>
                  <div className="grid gap-2">
                    {items.map((it, i) => (
                      <Fragment key={`${key}-${i}`}>
                      {key === "recipes" && (i === 0 || (items[i - 1].source === "public") !== (it.source === "public")) && (
                        <h3 data-testid={`search-recipes-subhead-${it.source === "public" ? "public" : "mine"}`} className={`text-xs uppercase tracking-wider font-black text-rf-ink-soft ${i ? "mt-4" : ""}`}>
                          {it.source === "public" ? "From the public directory" : "From your cookbooks"}
                        </h3>
                      )}
                      <div className="flex items-center gap-2 p-3 rounded-xl border border-rf-ink/10 bg-white hover:border-rf-ink/25 hover:shadow-sm transition-[border-color,box-shadow]">
                      <Link
                        to={it.url}
                        data-testid={`search-page-result-${key}-${i}`}
                        className="flex items-center gap-3 flex-1 min-w-0"
                      >
                        {it.image_url ? (
                          <img src={it.image_url} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0 border border-rf-ink/10" loading="lazy" />
                        ) : (
                          <div className="w-14 h-14 rounded-lg bg-rf-cream-alt border border-rf-ink/10 flex items-center justify-center shrink-0">
                            <Icon size={20} weight="duotone" className="text-rf-ink-soft" />
                          </div>
                        )}
                        {(key === "recipes" || key === "historical") && (
                          <span className="w-6 text-xs font-black text-rf-orange shrink-0 text-right" data-testid={`search-num-${key}-${i}`}>
                            {pickList.indexOf(it) + 1}
                          </span>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-sm md:text-base font-bold text-rf-ink truncate">{it.title}</div>
                          {it.subtitle && <div className="text-xs md:text-sm text-rf-ink-soft truncate">{it.subtitle}</div>}
                        </div>
                      </Link>
                      {(key === "recipes" || key === "historical") && it.id && <AddToCookbookButton item={it} />}
                      </div>
                      </Fragment>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
